// Anular una FACTURA del POS que ya se anuló en el portal del SRI (script 907).
//
// No es el «Anular» de la nota de venta ni una devolución: la NC es el camino
// normal (Devoluciones); esto es la excepción de la factura anulada en el
// portal. `pos_factura_anular_sri` registra la anulación fiscal, repone el
// stock, reversa la lealtad y ajusta el turno: si sigue abierto la venta
// simplemente sale del cierre; si ya se cerró, genera el contra-asiento por la
// cuenta del reembolso que se elige aquí.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { money, posRpc } from './shared';

export interface FacturaPosAnulable {
  doc_id: string; codigo: string; serie: string | null; cliente: string | null;
  total: number; turno: string;
}

interface Resultado {
  codigo: string; serie: string | null; turno: string; reembolso: string;
  movimientos_reversados: number; asiento_reverso: number | null; fecha_reverso: string | null;
}

export function AnularFacturaSriDialog({ doc, onClose, onDone }: {
  doc: FacturaPosAnulable; onClose: () => void; onDone: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [reembolso, setReembolso] = useState<'efectivo' | 'tarjeta'>('efectivo');
  const [verificado, setVerificado] = useState(false);

  const anular = useMutation({
    mutationFn: () => posRpc<Resultado>('pos_factura_anular_sri',
      { _venta_id: doc.doc_id, _motivo: motivo.trim(), _reembolso: reembolso }),
    onSuccess: (r) => {
      const turno = r.turno === 'abierta'
        ? ' El turno sigue abierto: la venta sale del cierre; devuelva el dinero por el mismo medio con que se pagó.'
        : ` El turno ya estaba cerrado: se registró el asiento de reverso nº ${r.asiento_reverso} (${r.fecha_reverso}).`;
      toast.success(`Factura ${r.serie ?? r.codigo} anulada. Stock repuesto (${r.movimientos_reversados} movimiento/s).${turno}`,
        { duration: 10000 });
      onDone(); onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const campo = 'w-full rounded-md border bg-background px-2.5 py-1.5 text-sm';
  const corto = motivo.trim().length < 10;

  return (
    <PanelIntegrado title="Anular factura anulada en el SRI" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          <strong className="font-mono">{doc.serie ?? doc.codigo}</strong>
          {doc.cliente && <> · {doc.cliente}</>} · {money(doc.total)} · turno {doc.turno}
        </p>
        <p className="text-xs text-muted-foreground">
          Esto no anula nada ante el SRI: registra una anulación que <strong>ya se hizo en el portal</strong>.
          Si el cliente solo devuelve la mercadería, use <strong>Devoluciones</strong> (nota de crédito).
          Se repone el stock, se reversan los puntos y la factura sale del cierre de caja y del ATS como anulada.
        </p>

        <label className="block text-xs text-muted-foreground">Motivo y constancia de la anulación en el portal *
          <textarea className={`${campo} mt-1`} rows={3} value={motivo} autoFocus
                    placeholder="Quién la anuló en el portal, cuándo y por qué (mínimo 10 caracteres)"
                    onChange={(e) => setMotivo(e.target.value)} />
          {corto && motivo.length > 0 && (
            <span className="text-[11px] text-amber-600">Faltan {10 - motivo.trim().length} caracteres</span>
          )}
        </label>

        <fieldset className="space-y-1">
          <legend className="text-xs text-muted-foreground mb-1">
            ¿Cómo se devuelve el dinero? <span className="italic">(solo cuenta si el turno ya está cerrado; con el
            turno abierto se devuelve por el mismo medio con que se pagó)</span>
          </legend>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={reembolso === 'efectivo'} onChange={() => setReembolso('efectivo')} />
            Efectivo (sale de la caja general)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={reembolso === 'tarjeta'} onChange={() => setReembolso('tarjeta')} />
            Reverso a la tarjeta con que se pagó
          </label>
        </fieldset>

        <label className="flex items-start gap-2 text-sm rounded-md border border-amber-500/50 bg-amber-500/5 p-3">
          <input type="checkbox" className="mt-0.5" checked={verificado} onChange={(e) => setVerificado(e.target.checked)} />
          <span>Consulté la clave de acceso en el SRI y la factura figura como <strong>ANULADA</strong>.
            Queda registrado a mi nombre.</span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" variant="destructive" isLoading={anular.isPending}
                  disabled={corto || !verificado} onClick={() => anular.mutate()}>
            Anular
          </Button>
        </div>
      </div>
    </PanelIntegrado>
  );
}
